import { css, Theme } from '@emotion/react'

import { border, typography } from '@arcade/web'

import { spacing } from '../../theme'
import { ArcadeThemeColors } from '../../theme/arcadeColors'

type ArcadeThemeColorKeys = keyof ArcadeThemeColors['component']

type InputVariants = 'default' | 'hover' | 'focus' | 'pressed' | 'selected' | 'invalid' | 'disabled'

type VariantStyles = {
  [K in 'color' | 'background' | 'border']: ArcadeThemeColorKeys
}

export const VARIANTS: Record<InputVariants, VariantStyles> = {
  default: {
    color: 'inputTextDefault',
    background: 'inputBackgroundDefault',
    border: 'inputBorderDefault',
  },
  hover: {
    color: 'inputTextDefault',
    background: 'inputBackgroundDefault',
    border: 'inputBorderHover',
  },
  focus: {
    color: 'inputTextDefault',
    background: 'inputBackgroundDefault',
    border: 'inputBorderSelected',
  },
  pressed: {
    color: 'inputTextDefault',
    background: 'inputBackgroundPressed',
    border: 'inputBorderDefault',
  },
  selected: {
    color: 'inputTextDefault',
    border: 'inputBorderSelected',
    background: 'inputBackgroundSelectedDefault',
  },
  invalid: {
    color: 'inputTextDefault',
    background: 'inputBackgroundDefault',
    border: 'inputBorderError',
  },
  disabled: {
    color: 'inputTextDisabled',
    background: 'inputBackgroundPressed',
    border: 'inputBorderDefault',
  },
}

export const getInputStyles = ({
  theme,
  hasLeadingIcon,
  hasTrailingIcon,
  isInvalid,
  disabled,
}: {
  theme: Theme
  hasLeadingIcon: boolean
  hasTrailingIcon: boolean
  isInvalid: boolean
  disabled: boolean
}) => {
  return css`
    /* common styles */
    ${typography.input}
    appearance: none;
    display: block;
    font-family: inherit;
    box-sizing: border-box;
    width: 100%;
    height: 3.25rem;

    /* Remove native stepper for input type = number */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      appearance: none;
    }

    &[type='number'] {
      /* needed to repeat to satisfy tslint need to set this value this for compatability */
      appearance: none;
    }

    /* icon(s) padding inside input */
    ${hasLeadingIcon && hasTrailingIcon
      ? `padding: ${spacing.s} 2.75rem;`
      : hasLeadingIcon
      ? `padding: ${spacing.s} ${spacing.m} ${spacing.s} 2.75rem;`
      : hasTrailingIcon
      ? `padding: ${spacing.s} ${spacing.xxl} ${spacing.s} ${spacing.m};`
      : // no icons
        `padding: ${spacing.s} ${spacing.m};`}

    /* default */
    color: ${theme.arcadeColors.component[VARIANTS['default'].color]};
    background: ${theme.arcadeColors.component[VARIANTS['default'].background]};
    border-radius: ${border.radius.small};
    border: 0.0625rem solid ${theme.arcadeColors.component[VARIANTS['default'].border]};

    /* hover */
    @media (hover: hover) {
      &:hover {
        ${!disabled &&
        `color: ${theme.arcadeColors.component[VARIANTS['hover'].color]};
      background: ${theme.arcadeColors.component[VARIANTS['hover'].background]};
      border-color: ${theme.arcadeColors.component[VARIANTS['hover'].border]}`};
      }
    }

    /* selected */
    &:focus {
      color: ${theme.arcadeColors.component[VARIANTS['focus'].color]};
      background: ${theme.arcadeColors.component[VARIANTS['focus'].background]};
      border-color: ${theme.arcadeColors.component[VARIANTS['focus'].border]};
      caret-color: ${theme.arcadeColors.component.inputCursor};
      outline: 0.0625rem solid ${theme.arcadeColors.component[VARIANTS['focus'].border]};
    }

    /* pressed */
    &:active {
      ${!disabled &&
      `color: ${theme.arcadeColors.component[VARIANTS['pressed'].color]};
      background: ${theme.arcadeColors.component[VARIANTS['pressed'].background]};
      border-color: ${theme.arcadeColors.component[VARIANTS['pressed'].border]};`}
    }

    /* error */
    &:invalid {
      color: ${theme.arcadeColors.component[VARIANTS['invalid'].color]};
      background: ${theme.arcadeColors.component[VARIANTS['invalid'].background]};
      ${!disabled && `border-color: ${theme.arcadeColors.component[VARIANTS['invalid'].border]};`}
    }

    ${isInvalid &&
    !disabled &&
    `color: ${theme.arcadeColors.component[VARIANTS['invalid'].color]};
      background: ${theme.arcadeColors.component[VARIANTS['invalid'].background]};
     border-color: ${theme.arcadeColors.component[VARIANTS['invalid'].border]};
    `}

    /* disabled */
    ${disabled &&
    `
      color: ${theme.arcadeColors.component[VARIANTS['disabled'].color]};
      background: ${theme.arcadeColors.component[VARIANTS['disabled'].background]};
      border-color: ${theme.arcadeColors.component[VARIANTS['disabled'].border]};
    `}

    /* Override default browser styling for auto filled inputs */
    &:-webkit-autofill {
      -webkit-text-fill-color: ${theme.arcadeColors.component.inputLabelDefault};

      box-shadow: 0 0 0 1000px ${theme.arcadeColors.component.inputBackgroundDefault} inset;
      background: ${theme.arcadeColors.component.inputBackgroundDefault};

      &:focus {
        color: ${theme.arcadeColors.component[VARIANTS['focus'].color]};
        background: ${theme.arcadeColors.component[VARIANTS['focus'].background]};
        border-color: ${theme.arcadeColors.component[VARIANTS['focus'].border]};
        caret-color: ${theme.arcadeColors.component.inputCursor};
        outline: 0.0625rem solid ${theme.arcadeColors.component[VARIANTS['focus'].border]};
        box-shadow: 0 0 0 1000px ${theme.arcadeColors.component.inputBackgroundDefault} inset;
      }

      &:disabled {
        -webkit-text-fill-color: ${theme.arcadeColors.component.inputLabelDisabled};
        box-shadow: 0 0 0 1000px ${theme.arcadeColors.component.inputBackgroundDefault} inset;
        background: ${theme.arcadeColors.component.inputBackgroundDefault};
      }

      &::first-line {
        ${typography.input}
      }
    }

    /* Removes the pseudo element from Microsoft Edge for password types */
    &::-ms-reveal {
      display: none;
    }
  `
}
